import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { isOperationalOrgView } from '../models/orgArtifactKind';
import type { OrganizationChangeApplyResult } from '../models/OrganizationChangeApplyResult';
import { OrgPlanningCommandBarrierBlockedError } from './orgPlanningCommandSequencer';
import { canReadOrgPlanningReview, type OrgPlanningReviewScope } from './orgPlanningReviewKey';
import { useOrgPlanningReviewScope } from './useOrgPlanningReviewScope';

export interface AtlasOperationalRecoveryReceipt {
  readonly changeSetId: string;
  readonly state: 'draft' | 'in_progress' | 'submitted' | 'awaiting_sync' | 'applied' | 'reconciled' | 'failed' | 'conflict';
  readonly destinationLabel?: string;
  readonly graphVersion: number;
  readonly executionId: string | null;
  readonly retryable: boolean;
  readonly originScenarioChangeSetId?: string | null;
}

export const atlasActiveOperationKey = (scope: OrgPlanningReviewScope) => [
  'atlas', 'active-operational-change', scope.folderId, scope.diagramId,
  scope.principalId, scope.actorOrgId, scope.sessionGeneration, scope.folderGeneration,
] as const;

export const atlasOperationStatusKey = (scope: OrgPlanningReviewScope, head: string | null) => [
  'atlas', 'operational-change-status', scope.folderId, scope.diagramId,
  scope.principalId, scope.actorOrgId, scope.sessionGeneration, scope.folderGeneration, head,
] as const;

const ACTIVE_OPERATIONAL_STATES = new Set([
  'in_progress',
  'submitted',
  'awaiting_sync',
]);

const TERMINAL_OPERATIONAL_STATES = new Set<OrganizationChangeApplyResult['state']>([
  'applied', 'reconciled', 'failed', 'conflict',
]);

const delay = (milliseconds: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, milliseconds);
});

/** Waits for one accepted external writeback without mutating source state. */
export async function waitForAtlasOperationalCompletion(
  request: (url: string, options?: RequestInit) => Promise<Response>,
  url: string,
  changeSetId: string,
  initial?: Pick<OrganizationChangeApplyResult, 'changeSetId' | 'state' | 'destination' | 'graphVersion'>,
): Promise<number> {
  let result = initial;
  while (!result || !TERMINAL_OPERATIONAL_STATES.has(result.state)) {
    if (result) await delay(5_000);
    const response = await request(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('The external organization change status is unavailable.');
    result = await response.json() as OrganizationChangeApplyResult;
    if (result.changeSetId !== changeSetId || !Number.isFinite(result.graphVersion)) {
      throw new Error('The external organization change status did not match the submitted change.');
    }
  }
  if (result.state === 'failed' || result.state === 'conflict') {
    throw new OrgPlanningCommandBarrierBlockedError(
      result.state === 'conflict'
        ? 'The source changed while the organization change was being pushed. Review the latest change before continuing.'
        : 'The organization change could not be pushed to the source. Review its status before continuing.',
    );
  }
  return result.graphVersion;
}

/** Stop discovery after the server answers with no operation or a terminal receipt. */
export function activeOperationalRefetchInterval(query: {
  state: {
    data?: AtlasOperationalRecoveryReceipt | null;
    error: Error | null;
  };
}, awaitingIntent: boolean): number | false {
  const errorStatus = (query.state.error as (Error & { status?: number }) | null)?.status;
  if (errorStatus === 404 || errorStatus === 409) return false;
  if (query.state.data === null) return false;
  if (query.state.data && !ACTIVE_OPERATIONAL_STATES.has(query.state.data.state)) return false;
  if (query.state.data && ACTIVE_OPERATIONAL_STATES.has(query.state.data.state)) return 5_000;
  return awaitingIntent ? 2_000 : false;
}

/** Read-only recovery. Discovery never opens a modal or executes an operation. */
export function useAtlasOperationalRecovery(folderId: string, diagramId: string, awaitingIntent = false) {
  const scope = useOrgPlanningReviewScope(folderId, diagramId);
  const diagram = useDiagramInfo(diagramId || undefined);
  const request = useAuthenticatedFetch();
  return useQuery({
    queryKey: atlasActiveOperationKey(scope),
    enabled: canReadOrgPlanningReview(scope) && isOperationalOrgView(diagram.data?.viewDefinition),
    staleTime: 5_000,
    queryFn: async ({ signal }): Promise<AtlasOperationalRecoveryReceipt | null> => {
      const response = await request(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/operational-changes/active`, {
        cache: 'no-store', signal,
      });
      if (!response.ok) {
        throw Object.assign(new Error('Operational recovery status unavailable'), { status: response.status });
      }
      return response.json();
    },
    refetchInterval: (query) => activeOperationalRefetchInterval(query, awaitingIntent),
  });
}
